/**
 * Monthly aggregate export rows for chart-based submission categories
 * (med_error, siren_bundle, needleandsharp). Nurse notes use nurseNotesExportRows.
 */
import type { ExportWorkbookSheet } from '../services/exportService';
import type { ReportingAggregateResult, ReportingSubmission } from '../services/dashboardData';
import { pooledMonthlyCompletenessPct } from '../services/dashboardData';
import type { ReportingMonth } from '../lib/reportingMonth';
import {
  isMedErrorSupervisionCategory,
  isSirenBundleCategory,
  reportingMetricColumnsForCategory,
} from '../lib/reportingMetricColumns';
import {
  METRICS_KEY_CHART1_CONSISTENCY_PCT,
  METRICS_KEY_OVERALL_CONSISTENCY_PCT,
} from '../lib/submissionMetrics';
import { formatThaiMonthYYBuddhist } from './dateThai';
import { buildSubmissionDetailExportData } from './submissionDetailExportRows';

const DISEASE_SCORE_KEY = 'disease_monthly_avg_score';

export type ChartCategoryMonthlyResult = {
  reportingMonth: ReportingMonth;
  result: ReportingAggregateResult;
};

function formatScore(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) ? value.toFixed(2) : '-';
}

function formatCount(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) ? String(Math.round(value)) : '-';
}

export function buildChartCategoryExportData(
  yearlyResults: ChartCategoryMonthlyResult[],
  months: ReportingMonth[],
  categoryId: string,
): { headers: string[]; rows: string[][] } {
  const resultMap = new Map(yearlyResults.map((entry) => [entry.reportingMonth, entry.result]));
  const metricColumns = reportingMetricColumnsForCategory(categoryId);
  // med_error and siren_bundle have no disease/consistency metrics in reporting UI/export.
  const includeDiseaseAndConsistency =
    !isMedErrorSupervisionCategory(categoryId) && !isSirenBundleCategory(categoryId);

  const headers = [
    'ลำดับ',
    'เดือน',
    ...metricColumns.flatMap((column) => [`${column.label} คะแนน`, `${column.label} จำนวน`]),
    'ร้อยละความสมบูรณ์',
    ...(includeDiseaseAndConsistency ? ['คะแนนรายโรค', 'ความสอดคล้อง'] : []),
  ];

  const rows = months.map((reportingMonth, index) => {
    const result = resultMap.get(reportingMonth);
    const metricCells = metricColumns.flatMap((column) => {
      const averages = result?.averages;
      const totals = result?.totals;
      const score = (averages?.[column.scoreKey as keyof typeof averages] ??
        averages?.[(column as { fallbackScoreKey?: string }).fallbackScoreKey ?? ''] ??
        null) as number | null;
      const count = (totals?.[column.countKey as keyof typeof totals] ??
        totals?.[(column as { fallbackCountKey?: string }).fallbackCountKey ?? ''] ??
        null) as number | null;
      return [formatScore(score), formatCount(count)];
    });
    const monthlyCompletenessPct = pooledMonthlyCompletenessPct(result);

    const base = [
      String(index + 1),
      formatThaiMonthYYBuddhist(reportingMonth),
      ...metricCells,
      monthlyCompletenessPct != null ? `${monthlyCompletenessPct.toFixed(2)}%` : '-',
    ];

    if (!includeDiseaseAndConsistency) return base;

    const diseaseScore =
      result?.averages[DISEASE_SCORE_KEY] != null && Number.isFinite(result.averages[DISEASE_SCORE_KEY])
        ? result.averages[DISEASE_SCORE_KEY]
        : null;
    const consistencyPct =
      result?.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT] != null &&
      Number.isFinite(result.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT])
        ? result.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT]
        : result?.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT] != null &&
            Number.isFinite(result.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT])
          ? result.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT]
          : null;

    return [
      ...base,
      formatScore(diseaseScore),
      consistencyPct != null ? `${consistencyPct.toFixed(2)}%` : '-',
    ];
  });

  return { headers, rows };
}

export type ChartCategoryExportWorkbookParams = {
  categoryId: string;
  categoryLabel: string;
  scopeLabel: string;
  periodLabel: string;
  fiscalYear: number;
  yearlyResults: ChartCategoryMonthlyResult[];
  months: ReportingMonth[];
  submissions: ReportingSubmission[];
  deptIdToName: Map<string, string>;
};

/** Summary sheet + detail sheet (HN/date/answers when the category stores them). */
export function buildChartCategoryExportWorkbook(
  params: ChartCategoryExportWorkbookParams,
): ExportWorkbookSheet[] {
  const { headers, rows } = buildChartCategoryExportData(
    params.yearlyResults,
    params.months,
    params.categoryId,
  );
  const detail = buildSubmissionDetailExportData({
    categoryId: params.categoryId,
    submissions: params.submissions,
    deptIdToName: params.deptIdToName,
  });

  const sheets: ExportWorkbookSheet[] = [
    {
      sheetName: 'คำอธิบาย',
      rows: [
        ['หมวดหมู่', params.categoryLabel],
        ['ขอบเขตหน่วยงาน', params.scopeLabel],
        ['ช่วงเวลา', params.periodLabel],
        ['ปีงบประมาณ', params.fiscalYear],
        ['เดือนที่ใช้คำนวณ', params.months.map(formatThaiMonthYYBuddhist).join(', ')],
        ['จำนวนรายการส่ง', params.submissions.length],
        [
          'หมายเหตุ',
          detail.headers.length
            ? 'ชีตสรุปรายเดือนเป็นค่าเฉลี่ยรายเดือน ชีตรายละเอียดมี HN/วันที่/คำตอบตามที่มีในแบบฟอร์ม'
            : 'ชีตสรุปรายเดือนเป็นค่าเฉลี่ยรายเดือน',
        ],
      ],
      columnWidths: [22, 80],
    },
    {
      sheetName: 'สรุปรายเดือน',
      rows: [headers, ...rows],
      freezeHeaderRows: 1,
      autoFilter: true,
    },
  ];

  if (detail.headers.length) {
    sheets.push({
      sheetName: 'รายละเอียด',
      rows:
        detail.rows.length > 0
          ? [detail.headers, ...detail.rows]
          : [detail.headers, ['ไม่มีข้อมูลรายละเอียดในช่วงที่เลือก', ...Array.from({ length: Math.max(0, detail.headers.length - 1) }, () => '—')]],
      freezeHeaderRows: 1,
      autoFilter: true,
    });
  }

  return sheets;
}
